// PipelineStageTab — generic tab for each pipeline stage.
// Handles three stage types seamlessly:
//   • Standard AI stages   → invite link to /stage-interview
//   • Questionnaire stages → invite email + /stage-questionnaire, auto-scores on submit
//   • Human interview      → AI/Human track assignment, Google Meet panel link, Hire/Hold/Reject
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import BulkPreviewModal from '../../components/common/modal/BulkPreviewModal';
import InterviewSlotsPanel from './InterviewSlotsPanel';
import HrStageDecision from './HrStageDecision';
import RoundRecord from './RoundRecord';
import AnswerKeyPanel, { PaperChoice, WrittenMarkForm, useWrittenPapers } from './AnswerKeyPanel';
import WrittenFollowUp from './WrittenFollowUp';
import api from '../../services/api';
import { useCopilotFocus } from '../../components/copilot/CopilotContext';
import config from '../../config/config';
import useMessagingHold, { isHeldError } from '../../hooks/useMessagingHold';
import MessagingHoldBanner from '../../components/common/messagingHold/MessagingHoldBanner';
import { Button, Card, CardHeader, Checkbox, EmptyState, Icon, Input, Select, StatusPill, Table } from '../../ds';
import { Loading, Note } from '../../components/page';
// ─── helpers ──────────────────────────────────────────────────────────────────
const STATUS_BADGE = {
  shortlisted: { label: 'Passed',      tone: 'success' },
  rejected:    { label: 'Failed',      tone: 'danger' },
  // Below the pass mark for the first time: waiting for the exit-filter
  // conductor, not out (HIR-TC1, 31 Oct 2026).
  exit_filter: { label: 'Exit filter', tone: 'warning' },
  redirected:  { label: 'Redirected',  tone: 'info' },
  completed:   { label: 'Completed',   tone: 'info' },
  in_progress: { label: 'In Progress', tone: 'warning' },
  not_started: { label: 'Not Started', tone: 'neutral' },
  null:        { label: 'Not Invited', tone: 'neutral' },
};
const INVITE_BADGE = {
  sent:      { label: 'Invited',   tone: 'info' },
  completed: { label: 'Submitted', tone: 'success' },
  pending:   { label: 'Pending',   tone: 'neutral' },
};
const HR_DECISION_BADGE = {
  hire:   { label: 'Hire',   tone: 'success' },
  hold:   { label: 'Hold',   tone: 'warning' },
  reject: { label: 'Reject', tone: 'danger' },
};
const TRACK_BADGE = {
  ai:    { label: 'AI Interview',    icon: 'robot' },
  human: { label: 'Human Interview', icon: 'users' },
};
const small = { fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' };
// A text-sized link-like button, for the small actions under a cell.
const linkButton = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontSize: 'var(--fs-caption)', color: 'var(--brand-primary-ink)', display: 'inline-flex', alignItems: 'center', gap: 4, textAlign: 'left' };
function ScorePill({ score, passingScore }) {
  if (score == null) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const pass = score >= passingScore;
  return (
    <StatusPill tone={pass ? 'success' : 'danger'} dot={false}>
      <span className="num">{score}%</span>
      <span style={{ opacity: 0.7, fontWeight: 400 }}>/ {passingScore}%</span>
    </StatusPill>
  );
}
const DISQUALIFIED_WHY = {
  left_exam: 'Left the exam window 3 times',
  violations: '5 serious violations',
};
const FACE_MATCH = {
  match: { tone: 'success', text: 'Photo matches the ID (AI)' },
  no_match: { tone: 'danger', text: 'Photo may NOT match the ID (AI) — please look' },
  unclear: { tone: 'warning', text: 'ID check unclear (AI) — please look' },
};
const CODE_STATE = {
  verified: 'Mobile confirmed by code',
  no_phone: 'No mobile on file — no code',
  not_sent: 'Code could not be sent',
  sent: 'Code sent, not confirmed',
};
const PHOTO_KIND = { start: 'Start', id_card: 'Photo ID', interval: 'During exam', violation: 'At a violation' };

/* The photos (S3, ten-minute links), fetched only when someone opens them. */
function ProctoringPhotos({ jobId, stageId, resumeId }) {
  const [photos, setPhotos] = React.useState(null);
  const [err, setErr] = React.useState('');
  const load = async () => {
    setErr('');
    try {
      const r = await api.get(`/jobs/${jobId}/pipeline-stage/${stageId}/candidates/${resumeId}/proctoring`);
      setPhotos(r.data?.data?.photos || []);
    } catch (e) { setErr(e.response?.data?.message || 'The photos could not be loaded.'); }
  };
  if (photos === null) {
    return (
      <div>
        <button onClick={load} style={linkButton}><Icon name="eye" size={12} /> Show photos</button>
        {err && <span style={{ color: 'var(--status-danger-text)', marginLeft: 8 }}>{err}</span>}
      </div>
    );
  }
  if (!photos.length) return <p style={{ margin: 0, color: 'var(--text-muted)' }}>No photos for this attempt.</p>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 6 }}>
      {photos.map((p) => (
        <a key={p.id} href={p.url || undefined} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: 'var(--text-2)' }}>
          {p.url
            ? <img src={p.url} alt={PHOTO_KIND[p.kind] || p.kind} style={{ width: '100%', height: 72, objectFit: 'cover', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }} />
            : <div style={{ height: 72, display: 'grid', placeItems: 'center', border: '1px dashed var(--border)' }}>—</div>}
          <div style={{ fontSize: 11 }}>
            {PHOTO_KIND[p.kind] || p.kind}{' · '}
            {p.takenAt ? new Date(p.takenAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
          </div>
        </a>
      ))}
    </div>
  );
}

function ProctoringReport({ examDetails, identity, jobId, stageId, resumeId }) {
  const [open, setOpen] = React.useState(false);
  if (!examDetails) return null;
  const { proctoringScore, riskLevel, violationCount, violations = [], disqualified, disqualifiedReason, timesLeftExam, microphone } = examDetails;
  const riskTone = { low: 'success', medium: 'warning', high: 'danger' }[riskLevel] || 'neutral';
  const face = FACE_MATCH[identity?.faceMatch?.result];
  return (
    <div style={{ marginTop: 4 }}>
      {disqualified && (
        <div><StatusPill tone="danger" dot={false}>Disqualified: {DISQUALIFIED_WHY[disqualifiedReason] || 'proctoring'}</StatusPill></div>
      )}
      <button onClick={() => setOpen(o => !o)} style={linkButton} aria-expanded={open}>
        <Icon name="shield-check" size={12} /> Proctoring {open ? '▲' : '▼'}
      </button>
      {open && (
        <div style={{ marginTop: 6, padding: 12, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--fs-caption)', display: 'grid', gap: 6, whiteSpace: 'normal' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <StatusPill tone="info" dot={false}>Score: {proctoringScore ?? '—'}/100</StatusPill>
            <StatusPill tone={riskTone} dot={false}>Risk: {riskLevel ?? '—'}</StatusPill>
            {timesLeftExam != null && <StatusPill tone={timesLeftExam ? 'warning' : 'neutral'} dot={false}>Left the exam: {timesLeftExam}×</StatusPill>}
            {microphone && <StatusPill tone={microphone === 'on' ? 'success' : 'danger'} dot={false}>Microphone: {microphone === 'on' ? 'on throughout' : 'went off'}</StatusPill>}
          </div>
          {identity && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {identity.otp && <StatusPill tone={identity.otp === 'verified' ? 'success' : 'warning'} dot={false}>{CODE_STATE[identity.otp] || identity.otp}{identity.phoneMasked ? ` (${identity.phoneMasked})` : ''}</StatusPill>}
              {face && <StatusPill tone={face.tone} dot={false}>{face.text}</StatusPill>}
            </div>
          )}
          {identity?.faceMatch?.note && <p style={{ margin: 0, color: 'var(--text-2)' }}>{identity.faceMatch.note}</p>}
          {/* Which violations, when — a count alone tells HR nothing they can
              weigh. "Not visible on camera ×3 around 10:02" reads very
              differently from "copy/paste ×3", and the difference is the
              decision. */}
          {violations.length ? (
            <ul style={{ listStyle: 'none', margin: 0, padding: '4px 0 0', display: 'grid', gap: 2 }}>
              {violations.map((v, i) => {
                const label = {
                  no_face:         'Not visible on camera',
                  multi_face:      'More than one person on camera',
                  tab_switch:      'Switched away from the exam tab',
                  fullscreen_exit: 'Left fullscreen',
                  copy_paste:      'Copy or paste attempted',
                  focus_lost:      'Exam window lost focus',
                  mic_off:         'Microphone turned off or disconnected',
                  background_speech: 'Voices in the background',
                  background_noise:  'Loud background noise',
                }[v.type] || v.description || v.type;
                const when = v.timestamp
                  ? new Date(v.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
                  : '';
                return (
                  <li key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: 'var(--text-2)' }}>
                    <span>{label}</span>
                    <span style={{ color: 'var(--text-muted)', flex: 'none' }}>{when}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p style={{ margin: 0, color: 'var(--text-2)' }}>{violationCount ?? 0} violation(s)</p>
          )}
          {jobId && resumeId && <ProctoringPhotos jobId={jobId} stageId={stageId} resumeId={resumeId} />}
        </div>
      )}
    </div>
  );
}
function StageSelectionDropdown({ resumeId, jobId, stageId, current, onChanged }) {
  const [saving, setSaving] = useState(false);
  const [value, setValue]   = useState(current || 'not_started');
  React.useEffect(() => { setValue(current || 'not_started'); }, [current]);
  const OPTIONS = [
    { value: 'shortlisted', label: '✓ Shortlisted' },
    { value: 'rejected',    label: '✗ Rejected' },
    { value: 'completed',   label: '● Completed' },
    { value: 'not_started', label: '○ Pending' },
  ];
  // The exit filter's conductor decides these, not this dropdown (HIR-TC1).
  if (current === 'exit_filter' || current === 'redirected') {
    const b = STATUS_BADGE[current];
    return <StatusPill tone={b.tone}>{b.label}</StatusPill>;
  }
  const handleChange = async (newVal) => {
    if (newVal === value) return;
    setSaving(true);
    try {
      await api.patch(`/jobs/${jobId}/pipeline-stage/${stageId}/candidates/${resumeId}/status`, { status: newVal });
      setValue(newVal);
      onChanged?.();
      toast.success('Status updated');
    } catch { toast.error('Failed to update status'); }
    finally { setSaving(false); }
  };
  return (
    <div style={{ minWidth: 150 }}>
      <Select value={value} onChange={handleChange} disabled={saving} options={OPTIONS} />
    </div>
  );
}
// ─── QuestionnaireAnswers inline accordion ────────────────────────────────────
function QuestionnaireAnswers({ answers }) {
  const [open, setOpen] = useState(false);
  if (!answers?.length) return null;
  return (
    <div style={{ marginTop: 4 }}>
      <button onClick={() => setOpen(o => !o)} style={linkButton} aria-expanded={open}>
        <Icon name="clipboard-text" size={12} /> {open ? 'Hide answers' : 'View answers'}
        <Icon name="caret-down" size={12} style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {open && (
        <div style={{ marginTop: 8, padding: 12, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--fs-caption)', display: 'grid', gap: 12, maxHeight: 224, overflowY: 'auto', whiteSpace: 'normal' }}>
          {answers.map((a, i) => (
            <div key={i} style={{ borderBottom: i < answers.length - 1 ? '1px solid var(--border)' : 'none', paddingBottom: i < answers.length - 1 ? 8 : 0 }}>
              <p style={{ margin: '0 0 4px', fontWeight: 500, color: 'var(--text-1)' }}>Q{i + 1}. {a.question}</p>
              {a.questionType === 'mcq' ? (
                <StatusPill tone={a.score > 0 ? 'success' : 'danger'} dot={false}>
                  Option {a.selectedOption != null ? a.selectedOption + 1 : '—'} — {a.score > 0 ? 'Correct' : 'Incorrect'}
                </StatusPill>
              ) : (
                <p style={{ margin: 0, color: 'var(--text-2)', background: 'var(--surface-1)', borderRadius: 'var(--radius-sm)', padding: 6, lineHeight: 1.5 }}>
                  {a.writtenAnswer || <em style={{ color: 'var(--text-muted)' }}>No answer</em>}
                </p>
              )}
              <p style={{ margin: '2px 0 0', color: 'var(--text-muted)' }}>{a.score}/{a.maxScore} pts</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
// ─── Human interview track assignment ────────────────────────────────────────
function TrackAssignment({ candidate, jobId, stageId, onChanged }) {
  const [assigning, setAssigning] = useState(false);
  const sr    = candidate.stageResult;
  const track = sr?.interviewTrack;
  const assign = async (newTrack) => {
    setAssigning(true);
    try {
      await api.post(`/human-interview/${jobId}/${stageId}/assign/${candidate._id}`, { track: newTrack });
      toast.success(newTrack === 'human'
        ? 'Assigned to Human Interview — give them a slot below to send the invitation'
        : 'Assigned to AI Interview track');
      onChanged?.();
    } catch (e) {
      toast.error(isHeldError(e) ? e.response.data.message : 'Failed to assign track');
    }
    finally { setAssigning(false); }
  };
  if (track) {
    const tb = TRACK_BADGE[track];
    return (
      <div style={{ display: 'grid', gap: 4, justifyItems: 'start' }}>
        <StatusPill tone="info" dot={false}><Icon name={tb.icon} size={12} /> {tb.label}</StatusPill>
        <button onClick={() => assign(track === 'ai' ? 'human' : 'ai')} disabled={assigning} style={linkButton}>
          Switch to {track === 'ai' ? 'Human' : 'AI'}
        </button>
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <span style={small}>Assign track:</span>
      <div style={{ display: 'flex', gap: 6 }}>
        <Button size="sm" variant="secondary" onClick={() => assign('ai')} disabled={assigning} icon={<Icon name="robot" />}>
          AI
        </Button>
        <Button size="sm" variant="secondary" onClick={() => assign('human')} disabled={assigning} icon={<Icon name="users" />}>
          Human
        </Button>
      </div>
    </div>
  );
}
// ─── main component ───────────────────────────────────────────────────────────
const PipelineStageTab = ({ stage, pipeline = [] }) => {
  const { id: jobId } = useParams();
  const navigate = useNavigate();
  // The school's candidate-message pause: invites and links work only for
  // test candidates while it is on (the server refuses the rest).
  const hold = useMessagingHold();
  const isQuestionnaire  = stage.type === 'questionnaire';
  const isHumanInterview = stage.type === 'human_interview';
  // A written test's papers and their answer keys (HIR-HR27).
  const written = useWrittenPapers(jobId, stage.stageId, !isQuestionnaire && !isHumanInterview);
  const [candidates, setCandidates]       = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showAll, setShowAll]             = useState(false);
  const [inviting, setInviting]           = useState(null);
  const [bulkInviting, setBulkInviting]   = useState(false);
  const [expiryDate, setExpiryDate]       = useState('');
  const [showExpiryFor, setShowExpiryFor] = useState(null);
  // Bulk email preview+send state
  const [selectedIds,  setSelectedIds]  = useState([]);
  const [previewing,   setPreviewing]   = useState(false);
  const [previewData,  setPreviewData]  = useState(null);
  const [sendingBulk,  setSendingBulk]  = useState(false);
  // Whether this stage's questionnaire actually has questions. A candidate
  // link points at the saved questionnaire, so inviting anyone before it
  // exists sends them to an error page. null = still checking / not a
  // questionnaire stage.
  const [questionnaireReady, setQuestionnaireReady] = useState(null);
  useEffect(() => {
    if (!isQuestionnaire) return;
    api.get(`/questionnaire/${jobId}/${stage.stageId}`)
      // Only a SAVED questionnaire counts (a persisted doc has an _id). The
      // server also returns unsaved drafts pre-filled with suggested
      // questions, and a draft nobody approved must not unlock invites.
      .then(r => setQuestionnaireReady(Boolean(r.data?.data?._id) && (r.data?.data?.questions || []).length > 0))
      .catch(() => setQuestionnaireReady(null));
  }, [isQuestionnaire, jobId, stage.stageId]);
  const inviteBlocked = isQuestionnaire && questionnaireReady === false;
  // The Copilot: this stage is on screen, and — when exactly one candidate is
  // ticked — that candidate ("Summarise this candidate").
  const focusedCandidate = selectedIds.length === 1 ? candidates.find(c => c._id === selectedIds[0]) : null;
  useCopilotFocus({
    stageId: stage.stageId,
    stageLabel: stage.label,
    ...(focusedCandidate ? { candidateId: focusedCandidate._id, candidateName: focusedCandidate.candidateName || '' } : {}),
  });
  // ── fetch ──────────────────────────────────────────────────────────────────
  const fetchCandidates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/jobs/${jobId}/pipeline-stage/${stage.stageId}/candidates`);
      const fetched    = res.data?.data?.candidates || [];
      const stageData  = res.data?.data?.stage || {};
      const passingScore = stageData.passingScore ?? 60;
      // Auto-sync stale statuses (exam or questionnaire completed but status not updated)
      const stale = fetched.filter(c => {
        const r = c.stageResult;
        // An attempt whose written answers are awaiting marking has no verdict
        // yet — never turn its partial score into a pass or fail here.
        return r && r.inviteStatus === 'completed' && r.score != null &&
          !['awaiting_marking', 'awaiting_key'].includes(r.examDetails?.markingStatus) &&
          !['shortlisted', 'rejected', 'exit_filter', 'redirected'].includes(r.status) &&
          !(r.hrRulings || []).length &&
          // Only a pass is synced from here. Below the pass mark is the
          // server's call (the exit filter, 31 Oct 2026), never a rejection
          // the browser makes up.
          r.score >= passingScore;
      });
      if (stale.length > 0) {
        await Promise.allSettled(stale.map(c => (
          api.patch(`/jobs/${jobId}/pipeline-stage/${stage.stageId}/candidates/${c._id}/status`, { status: 'shortlisted' })
        )));
        const res2 = await api.get(`/jobs/${jobId}/pipeline-stage/${stage.stageId}/candidates`);
        setCandidates(res2.data?.data?.candidates || []);
      } else {
        setCandidates(fetched);
      }
    } catch { toast.error('Failed to load candidates'); }
    finally { setLoading(false); }
  }, [jobId, stage.stageId]);
  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);
  // Each candidate's total out of 100, grade and status (HIR-TC1), or the
  // vacancy's one plain refusal when its weights are not set.
  const [totals, setTotals] = useState(null);
  // Fetched again only when something on this stage changed, not on every poll.
  const totalsKey = candidates.map((c) => `${c._id}:${c.stageResult?.status || ''}:${c.stageResult?.score ?? ''}:${(c.stageResult?.hrRulings || []).length}`).join('|');
  useEffect(() => {
    let live = true;
    api.get(`/candidate-scoring/vacancies/${jobId}/totals`)
      .then((r) => { if (live) setTotals(r.data?.data || null); })
      .catch(() => { if (live) setTotals(null); });
    return () => { live = false; };
  }, [jobId, totalsKey]);
  const totalOf = (id) => (totals?.ok ? (totals.candidates || []).find((t) => t.candidateId === id) : null);
  // Auto-poll — fast when invites are pending, slow always
  useEffect(() => {
    const hasPending = candidates.some(c => c.stageResult?.inviteStatus === 'sent');
    if (!hasPending) return;
    const t = setInterval(fetchCandidates, 15000);
    return () => clearInterval(t);
  }, [candidates, fetchCandidates]);
  useEffect(() => {
    const t = setInterval(fetchCandidates, 10000);
    return () => clearInterval(t);
  }, [fetchCandidates]);
  // ── send invite (questionnaire or standard) ────────────────────────────────
  const handleInvite = async (candidate) => {
    setInviting(candidate._id);
    try {
      if (isQuestionnaire) {
        await api.post(`/questionnaire/${jobId}/${stage.stageId}/invite/${candidate._id}`, {
          linkExpiry: expiryDate ? new Date(expiryDate).toISOString() : undefined,
        });
      } else {
        await api.post(`/jobs/${jobId}/pipeline-stage/${stage.stageId}/invite`, {
          resumeId: candidate._id,
          linkExpiry: expiryDate ? new Date(expiryDate).toISOString() : undefined,
        });
      }
      toast.success(`Invite sent to ${candidate.candidateName || candidate.email}`);
      setShowExpiryFor(null);
      setExpiryDate('');
      await fetchCandidates();
    } catch (e) {
      // Held (messages paused) or not ready — either way, show the reason.
      toast.error(isHeldError(e) ? e.response.data.message : (e.response?.data?.message || 'Failed to send invite'));
      await fetchCandidates();
    }
    finally { setInviting(null); }
  };
  // ── bulk invite all eligible uninvited candidates ──────────────────────────
  const handleBulkInvite = async () => {
    const uninvited = displayed.filter(c =>
      c.eligible && (!c.stageResult?.inviteStatus || c.stageResult.inviteStatus === 'pending')
    );
    if (!uninvited.length) { toast.info('All eligible candidates already invited'); return; }
    setBulkInviting(true);
    let sent = 0, failed = 0, held = 0, reason = '';
    for (const c of uninvited) {
      try {
        if (isQuestionnaire) {
          await api.post(`/questionnaire/${jobId}/${stage.stageId}/invite/${c._id}`, {});
        } else {
          await api.post(`/jobs/${jobId}/pipeline-stage/${stage.stageId}/invite`, { resumeId: c._id });
        }
        sent++;
      } catch (e) {
        if (isHeldError(e)) { held++; continue; }
        failed++;
        reason = reason || e.response?.data?.message || '';
        // Not ready means not ready for everyone — stop rather than repeat it.
        if (e.response?.data?.code === 'stage_not_ready') break;
      }
    }
    if (sent || held) toast.success(`Invites sent: ${sent}${held ? ` · ${held} not sent: candidate messages are paused` : ''}`);
    if (failed) toast.error(reason || `${failed} invite(s) failed`);
    setBulkInviting(false);
    await fetchCandidates();
  };
  // ── bulk email preview → approve → send ───────────────────────────────────
  const toggleSelect = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = (displayedList) =>
    setSelectedIds(selectedIds.length === displayedList.length ? [] : displayedList.map(c => c._id));
  const handlePreviewEmails = async () => {
    if (!selectedIds.length) { toast.error('Select at least one candidate'); return; }
    setPreviewing(true);
    try {
      const res = await api.post('/invites/bulk/preview', {
        resumeIds: selectedIds,
        stageId:   stage.stageId,
        jobId,
      });
      setPreviewData(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate email previews');
    } finally {
      setPreviewing(false);
    }
  };
  const handleApproveAndSend = async (approvedDrafts) => {
    setSendingBulk(true);
    try {
      const res = await api.post('/invites/bulk', {
        resumeIds: approvedDrafts.map(d => d.resumeId),
        stageId:   stage.stageId,
        jobId,
        drafts:    approvedDrafts,
      });
      const { sent, failed, total } = res.data.data;
      toast.success(`Sent ${sent}/${total} invite emails${failed > 0 ? ` (${failed} failed)` : ''}`);
      setPreviewData(null);
      setSelectedIds([]);
      await fetchCandidates();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bulk send failed');
    } finally {
      setSendingBulk(false);
    }
  };
  // ── link per stage type ────────────────────────────────────────────────────
  const stageLink = (resumeId) => isQuestionnaire
    ? `${config.baseUrl}/stage-questionnaire/${jobId}/${resumeId}/${stage.stageId}`
    : `${config.baseUrl}/stage-interview/${jobId}/${resumeId}/${stage.stageId}`;
  // Exam and interview links are signed by the server (the candidate's own
  // link to this stage); the browser can no longer build one.
  const copyStageLink = async (resumeId) => {
    try {
      let link = stageLink(resumeId);
      if (!isQuestionnaire) {
        const r = await api.get(`/jobs/${jobId}/pipeline-stage/${stage.stageId}/candidates/${resumeId}/link`);
        link = r.data?.data?.link;
      }
      await navigator.clipboard.writeText(link);
      toast.success('Link copied!');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not copy the link');
    }
  };
  const [retrying, setRetrying] = useState('');
  const retryMarking = async (resumeId) => {
    setRetrying(resumeId);
    try {
      await api.post(`/jobs/${jobId}/pipeline-stage/${stage.stageId}/candidates/${resumeId}/retry-marking`);
      toast.success('Marked.');
      await fetchCandidates();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Marking is still unavailable. Try again shortly.');
    } finally { setRetrying(''); }
  };
  const panelLink = (resumeId) =>
    `/jobs/${jobId}/human-interview/${stage.stageId}/panel/${resumeId}`;
  // ── display subset ─────────────────────────────────────────────────────────
  const displayed      = showAll ? candidates : candidates.filter(c => c.eligible);
  const eligibleCount  = candidates.filter(c => c.eligible).length;
  const passedCount    = candidates.filter(c => c.stageResult?.status === 'shortlisted').length;
  const decidedCount   = candidates.filter(c => c.stageResult?.hrDecision).length;
  const waitingCount   = candidates.filter(c => c.stageResult?.status === 'exit_filter').length;
  if (loading) return <Card><Loading label="Loading candidates" /></Card>;
  const allShownSelected = selectedIds.length > 0 && selectedIds.length === displayed.length;
  // A candidate who has not reached this stage is shown dimmed.
  const dim = (c, node) => <div style={{ opacity: c.eligible ? 1 : 0.5 }}>{node}</div>;
  const columns = [
    ...(!isHumanInterview ? [{
      key: 'pick', label: '',
      render: (c) => (
        <Checkbox checked={selectedIds.includes(c._id)} onChange={() => toggleSelect(c._id)} />
      ),
    }] : []),
    { key: 'candidate', label: 'Candidate', render: (c) => dim(c, (
      <div style={{ whiteSpace: 'normal' }}>
        <div style={{ fontWeight: 500, color: 'var(--text-1)' }}>{c.candidateName || '—'}</div>
        <div style={small}>{c.email}</div>
        {!c.eligible && c.questionnaireGate === 'expired' && (
          <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ ...small, color: 'var(--status-warning-text)', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Icon name="warning-circle" size={12} /> Questionnaire deadline expired
            </span>
            <Button
              size="sm"
              variant="secondary"
              icon={<Icon name="thumbs-up" />}
              onClick={async () => {
                try {
                  await api.post(`/questionnaire/${jobId}/manual-approve/${c._id}`);
                  toast.success('Candidate manually approved for pipeline');
                  fetchCandidates();
                } catch { toast.error('Failed to approve'); }
              }}
            >
              Approve
            </Button>
          </div>
        )}
        {!c.eligible && c.questionnaireGate === 'pending' && (
          <span style={{ ...small, color: 'var(--status-info-text)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
            <Icon name="calendar" size={12} /> Questionnaire pending
            {c.questionnaireDeadline && ` — due ${new Date(c.questionnaireDeadline).toLocaleDateString()}`}
          </span>
        )}
        {!c.eligible && c.questionnaireGate === 'not_sent' && (
          <span style={{ ...small, fontWeight: 500, marginTop: 4, display: 'block' }}>Did not pass previous stage</span>
        )}
        {!c.eligible && !['expired','pending','not_sent'].includes(c.questionnaireGate) && (
          <span style={{ ...small, color: 'var(--status-danger-text)', fontWeight: 500, marginTop: 4, display: 'block' }}>Did not pass previous stage</span>
        )}
      </div>
    )) },
    // Total out of 100, grade and status across the stages (HIR-TC1).
    ...(totals ? [{ key: 'total', label: 'Total', render: (c) => {
      if (!totals.ok) return <span style={small} title={totals.refusal}>—</span>;
      const t = totalOf(c._id);
      if (!t) return <span style={small}>—</span>;
      return (
        <div style={{ display: 'grid', gap: 2, justifyItems: 'start' }} title={t.statusWhy || ''}>
          <span className="num" style={{ fontWeight: 600, color: 'var(--text-1)' }}>
            {t.total == null ? '—' : t.total}{t.grade ? ` · ${t.grade}` : ''}
          </span>
          <span style={{ ...small, textTransform: 'capitalize' }}>{t.status}</span>
        </div>
      );
    } }] : []),
    // Score (not for human interview)
    ...(!isHumanInterview ? [{ key: 'score', label: 'Score', render: (c) => {
      const sr = c.stageResult;
      return dim(c, (
        <div>
          {sr?.examDetails?.markingStatus === 'awaiting_key' ? (
            <div style={{ display: 'grid', gap: 4, justifyItems: 'start', whiteSpace: 'normal' }}>
              <StatusPill tone="warning">Held: key not confirmed</StatusPill>
              <span style={small}>Answers kept, not marked, until a named person confirms the paper's key (Answer keys, above).</span>
            </div>
          ) : sr?.examDetails?.markingStatus === 'awaiting_marking' && sr?.examDetails?.markedBy === 'person' ? (
            <div style={{ display: 'grid', gap: 4, justifyItems: 'start', whiteSpace: 'normal' }}>
              <StatusPill tone="warning">Written answer to mark</StatusPill>
              <span style={small}>Marked by a person, never the AI — no pass or fail yet.</span>
              <WrittenMarkForm resumeId={c._id} stageId={stage.stageId} stageResult={sr} onChanged={() => { fetchCandidates(); written.reload(); }} />
            </div>
          ) : sr?.examDetails?.markingStatus === 'awaiting_marking' ? (
            <div style={{ display: 'grid', gap: 4, justifyItems: 'start', whiteSpace: 'normal' }}>
              <StatusPill tone="warning">Awaiting marking</StatusPill>
              <span style={small}>Written answers not yet marked — no pass or fail yet.</span>
              <button onClick={() => retryMarking(c._id)} disabled={retrying === c._id} style={{ ...linkButton, fontWeight: 600 }}>
                {retrying === c._id ? 'Marking…' : 'Retry marking'}
              </button>
            </div>
          ) : (
            <ScorePill score={sr?.score} passingScore={stage.passingScore} />
          )}
          {isQuestionnaire && sr?.questionnaireDetails?.answers && (
            <QuestionnaireAnswers answers={sr.questionnaireDetails.answers} />
          )}
          {!isQuestionnaire && <ProctoringReport examDetails={sr?.examDetails} identity={sr?.identity} jobId={jobId} stageId={stage.stageId} resumeId={c._id} />}
          {written.choiceNeeded && !sr?.examDetails && (
            <PaperChoice resumeId={c._id} stageId={stage.stageId} papers={written.papers} current={sr?.paperChoice?.paperName || ''} onChanged={fetchCandidates} />
          )}
        </div>
      ));
    } }] : []),
    // Track (human interview) OR Status badge
    { key: 'status', label: isHumanInterview ? 'Track' : 'Status', render: (c) => {
      const sr = c.stageResult;
      const badge = STATUS_BADGE[sr?.status || 'null'] || STATUS_BADGE['null'];
      const hrDecision = sr?.hrDecision;
      return dim(c, isHumanInterview ? (
        <div style={{ display: 'grid', gap: 4, justifyItems: 'start' }}>
          <TrackAssignment
            candidate={c}
            jobId={jobId}
            stageId={stage.stageId}
            onChanged={fetchCandidates}
          />
          {sr?.slot?.startsAt && (
            <span style={{ ...small, color: 'var(--text-2)' }}>
              {new Date(sr.slot.startsAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
              {sr.slot.rescheduleCount > 0 && <span style={{ color: 'var(--status-warning-text)' }}> · moved by candidate</span>}
            </span>
          )}
          {hrDecision && (
            <StatusPill tone={HR_DECISION_BADGE[hrDecision]?.tone || 'neutral'}>
              {HR_DECISION_BADGE[hrDecision]?.label}
            </StatusPill>
          )}
          <HrStageDecision resumeId={c._id} stageId={stage.stageId} stageResult={sr} onChanged={fetchCandidates} />
          <RoundRecord kind={stage.weightKey} resumeId={c._id} stageId={stage.stageId} stageResult={sr} onChanged={fetchCandidates} />
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 4, justifyItems: 'start' }}>
          <StatusPill tone={badge.tone}>{badge.label}</StatusPill>
          <HrStageDecision resumeId={c._id} stageId={stage.stageId} stageResult={sr} onChanged={fetchCandidates} />
        </div>
      ));
    } },
    // Selection dropdown
    { key: 'selection', label: 'Selection', render: (c) => dim(c, (
      <StageSelectionDropdown
        resumeId={c._id}
        jobId={jobId}
        stageId={stage.stageId}
        current={c.stageResult?.status}
        onChanged={fetchCandidates}
      />
    )) },
    // Invite status (not for human interview)
    ...(!isHumanInterview ? [{ key: 'invite', label: 'Invite', render: (c) => {
      const sr = c.stageResult;
      const inviteBadge = sr?.inviteStatus ? (INVITE_BADGE[sr.inviteStatus] || INVITE_BADGE.pending) : INVITE_BADGE.pending;
      return dim(c, (
        <div style={{ display: 'grid', gap: 2, justifyItems: 'start' }}>
          <StatusPill tone={inviteBadge.tone}>{inviteBadge.label}</StatusPill>
          {sr?.inviteSentAt && (
            <span style={small}>{new Date(sr.inviteSentAt).toLocaleDateString()}</span>
          )}
        </div>
      ));
    } }] : []),
    // Actions
    { key: 'actions', label: 'Actions', render: (c) => {
      const sr = c.stageResult;
      const isInviting  = inviting === c._id;
      const alreadySent = sr?.inviteStatus === 'sent' || sr?.inviteStatus === 'completed';
      if (!c.eligible) return null;
      return (
        <div style={{ display: 'grid', gap: 6, justifyItems: 'start' }}>
          {/* Human interview: open panel button */}
          {isHumanInterview && sr?.interviewTrack === 'human' && (
            <Button size="sm" onClick={() => navigate(panelLink(c._id))} icon={<Icon name="eye" />}>
              Open Panel
            </Button>
          )}
          {/* A completed round cannot be re-invited — the
              candidate would only meet the "already
              completed" wall, which is why Resend used to be
              a door to nowhere. What HR can do is reopen the
              attempt, explicitly: the old report is kept and
              marked superseded, the action is logged, and a
              fresh invite follows. */}
          {!isHumanInterview && sr?.inviteStatus === 'completed' && (
            <Button
              size="sm"
              variant="secondary"
              icon={<Icon name="arrow-clockwise" />}
              onClick={async () => {
                if (!window.confirm(`Allow ${c.candidateName || 'this candidate'} to retake this stage? The completed attempt is kept on record and a fresh invite can then be sent.`)) return;
                try {
                  await api.post(`/jobs/${jobId}/pipeline-stage/${stage.stageId}/candidates/${c._id}/allow-retake`);
                  toast.success('Retake allowed — send a fresh invite.');
                  fetchCandidates();
                } catch (e) {
                  toast.error(e.response?.data?.message || 'Could not allow a retake');
                }
              }}
            >
              Allow retake
            </Button>
          )}
          {/* Standard + questionnaire: send invite */}
          {!isHumanInterview && sr?.inviteStatus !== 'completed' && (
            <>
              {showExpiryFor === c._id ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 210 }}>
                    <Input type="datetime-local" value={expiryDate}
                      onChange={e => setExpiryDate(e.target.value)} />
                  </div>
                  <Button size="sm" onClick={() => handleInvite(c)} disabled={isInviting}
                    icon={<Icon name={isInviting ? 'spinner' : 'paper-plane-right'} spin={isInviting} />}>
                    Send
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowExpiryFor(null)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button size="sm" variant="secondary" onClick={() => setShowExpiryFor(c._id)}
                  disabled={inviteBlocked} icon={<Icon name="paper-plane" />}>
                  {alreadySent ? 'Resend' : 'Send Invite'}
                </Button>
              )}
              <button
                onClick={() => {
                  if (inviteBlocked) { toast.error('Build the questionnaire first — this link would show the candidate an error.'); return; }
                  if (hold.paused && !c.isTestCandidate) { toast.error('Candidate messages are paused — this link would not open for this candidate. Only test candidates can use stage links.'); return; }
                  copyStageLink(c._id);
                }}
                style={linkButton}>
                Copy link
              </button>
            </>
          )}
        </div>
      );
    } },
  ];
  return (
    <div style={{ display: 'grid', gap: 24, minWidth: 0 }}>
      <MessagingHoldBanner paused={hold.paused} loading={hold.loading} />
      {/* ── Header ── */}
      <Card>
        <CardHeader
          title={stage.label}
          subtitle={isQuestionnaire ? 'Questionnaire' : isHumanInterview ? 'Human Interview' : undefined}
          action={
            <Button size="sm" variant="ghost" onClick={fetchCandidates} title="Refresh" aria-label="Refresh" icon={<Icon name="arrow-clockwise" />} />
          }
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220, fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>
            {!isHumanInterview && <>Pass score: <span className="num" style={{ fontWeight: 600, color: 'var(--text-1)' }}>{stage.passingScore}%</span></>}
            {stage.brief && <span style={{ marginLeft: 12, fontStyle: 'italic', color: 'var(--text-muted)' }}>— {stage.brief}</span>}
          </div>
          <StatusPill tone="info" dot={false}><Icon name="users" size={12} /> {eligibleCount} eligible</StatusPill>
          {!isHumanInterview && (
            <StatusPill tone="success" dot={false}><Icon name="check-circle" size={12} /> {passedCount} passed</StatusPill>
          )}
          {isHumanInterview && decidedCount > 0 && (
            <StatusPill tone="neutral" dot={false}><Icon name="users" size={12} /> {decidedCount} decided</StatusPill>
          )}
          {/* Bulk invite — questionnaire or standard AI stages */}
          {!isHumanInterview && (
            <Button size="sm" onClick={handleBulkInvite} disabled={bulkInviting || inviteBlocked}
              icon={<Icon name={bulkInviting ? 'spinner' : 'paper-plane'} spin={bulkInviting} />}>
              Invite All Eligible
            </Button>
          )}
          {/* Preview & Send — AI-drafted personalised emails */}
          {!isHumanInterview && (
            <Button size="sm" variant="secondary" onClick={handlePreviewEmails} disabled={previewing || !selectedIds.length || inviteBlocked}
              icon={<Icon name={previewing ? 'spinner' : 'eye'} spin={previewing} />}>
              {previewing ? 'Drafting…' : `Preview & Send${selectedIds.length ? ` (${selectedIds.length})` : ''}`}
            </Button>
          )}
        </div>
      </Card>
      {/* ── No questionnaire yet: say so before anyone is invited to it ── */}
      {inviteBlocked && (
        <Note tone="warning">
          <strong>This stage has no questionnaire yet.</strong>{' '}
          A candidate opening its link would see an error, so invites and links are
          disabled. Build the questions in the <strong>Assessment
          Pipeline</strong> tab — open this stage's set-up and save the questionnaire —
          and this unlocks by itself.
        </Note>
      )}

      {/* ── Below the pass mark, waiting for the exit-filter conductor (HIR-TC1) ── */}
      {waitingCount > 0 && (
        <Note tone="info">
          {waitingCount} below the pass mark here {waitingCount === 1 ? 'is' : 'are'} waiting
          in the exit filter. They are not out and have not been told anything; the school's
          exit-filter conductor decides. <button type="button" style={linkButton} onClick={() => navigate('/exit-filter')}>Open the exit filter</button>
        </Note>
      )}

      {/* ── A school paper's answer key, its fixes, and several papers (HIR-HR27) ── */}
      {!isQuestionnaire && !isHumanInterview && (
        <AnswerKeyPanel papers={written.papers} choiceNeeded={written.choiceNeeded} onChanged={() => { written.reload(); fetchCandidates(); }} />
      )}

      {/* ── The school's response sheet, strong specialists, demo panellists (HIR-SYS3, HR28, HR29) ── */}
      {!isQuestionnaire && !isHumanInterview && written.papers.length > 0 && (
        <WrittenFollowUp
          jobId={jobId}
          stage={stage}
          demoStages={pipeline.filter((s) => s.type === 'human_interview' && s.enabled !== false && (s.order ?? 0) > (stage.order ?? 0))}
          onChanged={() => { written.reload(); fetchCandidates(); }}
        />
      )}

      {/* ── Interview round: slots, panel, place or link (HIR-HR4) ── */}
      {isHumanInterview && (
        <InterviewSlotsPanel jobId={jobId} stage={stage} candidates={candidates} onChanged={fetchCandidates} />
      )}

      <Card padding="0">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
          {/* ── Filter toggle ── */}
          <Button size="sm" variant={!showAll ? 'primary' : 'secondary'} onClick={() => setShowAll(false)}>
            Eligible ({eligibleCount})
          </Button>
          <Button size="sm" variant={showAll ? 'primary' : 'secondary'} onClick={() => setShowAll(true)}>
            All ({candidates.length})
          </Button>
          <span style={{ flex: 1 }} />
          {/* ── Select-all row ── */}
          {!isHumanInterview && (
            <>
              <Checkbox
                checked={allShownSelected}
                indeterminate={!allShownSelected && selectedIds.length > 0}
                onChange={() => toggleSelectAll(displayed)}
                label={selectedIds.length > 0 ? `${selectedIds.length} selected` : 'Select all'}
              />
              {selectedIds.length > 0 && (
                <Button size="sm" variant="ghost" aria-label="Clear selection" icon={<Icon name="x" />} onClick={() => setSelectedIds([])} />
              )}
            </>
          )}
        </div>
        {/* ── Table ── */}
        {displayed.length === 0 ? (
          <EmptyState
            icon={<Icon name="users" size={36} />}
            title={showAll ? 'No candidates yet' : 'No candidates have passed the previous stage yet'}
            action={!showAll ? (
              <div><Button size="sm" variant="secondary" onClick={() => setShowAll(true)}>Show all candidates</Button></div>
            ) : null}
          />
        ) : (
          <Table columns={columns} rows={displayed} getRowId={(c) => c._id} />
        )}
      </Card>
      {previewData && (
        <BulkPreviewModal
          drafts={previewData.drafts}
          stage={previewData.stage}
          job={previewData.job}
          onApprove={handleApproveAndSend}
          onClose={() => setPreviewData(null)}
          sending={sendingBulk}
        />
      )}
    </div>
  );
};
export default PipelineStageTab;
